/**
 * Dashboard Type Definitions
 *
 * Types for the admin dashboard export statistics.
 * Matches the response shape from GET /admin/export-stats endpoint.
 */

// ============================================
// EXPORT STATS (from /admin/export-stats)
// ============================================

export interface AttorneyExportStats {
  prof_id: string;
  name: string;
  email: string;
  exported_hours: number;
  unexported_hours: number;
  total_hours: number;
  exported_pct: number;
}

export interface ExportStatsTotals {
  exported_hours: number;
  unexported_hours: number;
  total_hours: number;
  exported_pct: number;
  unexported_pct: number;
}

export interface ExportStats {
  startDate: string | null;
  endDate: string | null;
  attorneys: AttorneyExportStats[];
  totals: ExportStatsTotals;
}

// ============================================
// TIMEFRAME OPTIONS
// ============================================

export interface TimeframeOption {
  value: string;
  label: string;
}

export const TIMEFRAME_OPTIONS: TimeframeOption[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "3days", label: "Last 3 Days" },
  { value: "7days", label: "Last 7 Days" },
  { value: "thismonth", label: "This Month" },
  { value: "30days", label: "Last 30 Days" },
  { value: "90days", label: "Last 90 Days" },
  { value: "1year", label: "Last Year" },
  { value: "all", label: "All Time" },
];

export function getTimeframeLabel(timeframe: string): string {
  return TIMEFRAME_OPTIONS.find((t) => t.value === timeframe)?.label ?? "Today";
}

/**
 * Converts a timeframe string to start and end dates.
 * Used to call the backend API with date range parameters.
 */
export function getDateRangeFromTimeframe(timeframe: string): {
  startDate: string;
  endDate: string;
} {
  const today = new Date();
  const endDate = today.toISOString().split("T")[0];

  let startDate: string;

  switch (timeframe) {
    case "today":
      startDate = endDate;
      break;
    case "yesterday": {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      startDate = yesterday.toISOString().split("T")[0];
      return { startDate, endDate: startDate }; // Both dates are yesterday
    }
    case "3days": {
      const threeDaysAgo = new Date(today);
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 2);
      startDate = threeDaysAgo.toISOString().split("T")[0];
      break;
    }
    case "7days": {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 6);
      startDate = weekAgo.toISOString().split("T")[0];
      break;
    }
    case "thismonth": {
      const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      startDate = firstOfMonth.toISOString().split("T")[0];
      break;
    }
    case "30days": {
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29);
      startDate = thirtyDaysAgo.toISOString().split("T")[0];
      break;
    }
    case "90days": {
      const ninetyDaysAgo = new Date(today);
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 89);
      startDate = ninetyDaysAgo.toISOString().split("T")[0];
      break;
    }
    case "1year": {
      const yearAgo = new Date(today);
      yearAgo.setFullYear(yearAgo.getFullYear() - 1);
      startDate = yearAgo.toISOString().split("T")[0];
      break;
    }
    case "all":
      // Use a very old date for "all time"
      startDate = "2020-01-01";
      break;
    default:
      startDate = endDate; // Default to today
  }

  return { startDate, endDate };
}
